'use client';

import { cn } from '@core/utils';

import { BREAKPOINTS } from '@/constants';
import { AD_MOCK_MODE, isAdHidden, useAd } from '@/hooks/useAd';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { AdBadge } from './AdBadge';
import {
  AD_SLOT_DEBUG_PLACEHOLDER_ENABLED,
  AdSlotDebugPlaceholder,
} from './AdSlotDebugPlaceholder';

export interface NativeAdSlotConfig {
  //* 빈 string은 콘솔 ID 미발급 슬롯 — 운영에서는 null, 개발에서는 위치 확인용 placeholder.
  unitIdM: string;
  unitIdP: string;
  mWidth: number;
  mHeight: number;
  pWidth: number;
  pHeight: number;
  rounded?: 'medium' | 'large';
}

interface NativeAdSlotProps extends NativeAdSlotConfig {
  as?: 'div' | 'li';
  className?: string;
}

export const NativeAdSlot = ({
  unitIdM,
  unitIdP,
  mWidth,
  mHeight,
  pWidth,
  pHeight,
  as = 'div',
  className,
  rounded = 'large',
}: NativeAdSlotProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const unitId = isDesktop ? unitIdP : unitIdM;
  const width = isDesktop ? pWidth : mWidth;
  const height = isDesktop ? pHeight : mHeight;

  if (!unitId && AD_SLOT_DEBUG_PLACEHOLDER_ENABLED) {
    const slot = (
      <AdSlotDebugPlaceholder
        width={width}
        height={height}
        rounded={rounded}
        className={className}
      />
    );

    if (as === 'li') return <li>{slot}</li>;

    return slot;
  }

  if (!unitId) return null;

  return (
    <NativeAdSlotInner
      key={unitId}
      unitId={unitId}
      width={width}
      height={height}
      as={as}
      className={className}
      rounded={rounded}
    />
  );
};

interface NativeAdSlotInnerProps {
  unitId: string;
  width: number;
  height: number;
  as: 'div' | 'li';
  className?: string;
  rounded: 'medium' | 'large';
}

const NativeAdSlotInner = ({
  unitId,
  width,
  height,
  as,
  className,
  rounded,
}: NativeAdSlotInnerProps) => {
  const { containerRef, adState } = useAd(unitId, { type: 'native' });

  if (isAdHidden(adState) && !AD_SLOT_DEBUG_PLACEHOLDER_ENABLED) return null;

  const slot = isAdHidden(adState) ? (
    <AdSlotDebugPlaceholder
      width={width}
      height={height}
      rounded={rounded}
      className={className}
    />
  ) : (
    <div
      ref={containerRef}
      className={cn(
        'relative overflow-hidden',
        rounded === 'medium' ? 'rounded-medium' : 'rounded-large',
        'bg-Gray-100',
        adState === 'idle' && 'invisible',
        adState === 'loading' && 'animate-pulse',
        className,
      )}
      style={{ width, height }}
    >
      <AdBadge className={cn(adState !== 'adReceived' && 'invisible')} />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        data-adrop-native="asset"
        src={
          AD_MOCK_MODE
            ? `https://placehold.co/${width}x${height}/e2e8f0/64748b?text=AD`
            : undefined
        }
        alt=""
        className="size-full border-0 object-cover"
      />
      <p data-adrop-native="headline" className="hidden" />
      <p data-adrop-native="body" className="hidden" />
    </div>
  );

  if (as === 'li') return <li>{slot}</li>;

  return slot;
};
